import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import eventosGanttService from '../services/eventosGanttService';
import ActividadModal from './ActividadModal';
import TareaEventoModal from './TareaEventoModal';
import Loading from './Loading';
import './GanttChart.css';

/**
 * Helper para parsear fechas en formato YYYY-MM-DD como hora local
 * Evita el problema de interpretación UTC que causa desfase de 1 día
 */
const parseDateLocal = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Componente Gantt Custom - Timeline visual de actividades y tareas
 * Mejoras: Drag & drop con edge dragging, actualización parcial sin reload total
 * @param {number} eventoId - ID del evento (opcional si se pasan actividadesExternas y tareasExternas)
 * @param {array} actividadesExternas - Array de actividades pre-cargadas (opcional)
 * @param {array} tareasExternas - Array de tareas pre-cargadas (opcional)
 */
const GanttChart = ({ eventoId, actividadesExternas = null, tareasExternas = null }) => {
  const [actividades, setActividades] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [mostrarDetalles, setMostrarDetalles] = useState(true);
  const [expandedActivities, setExpandedActivities] = useState({});

  // Modales
  const [actividadModalOpen, setActividadModalOpen] = useState(false);
  const [tareaModalOpen, setTareaModalOpen] = useState(false);
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  const [actividadPadreParaTarea, setActividadPadreParaTarea] = useState(null);
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);

  // Ref para el contenedor de scroll
  const ganttWrapperRef = useRef(null);

  // Estado de drag mejorado
  const [dragState, setDragState] = useState({
    isDragging: false,
    type: null, // 'move', 'startEdge', 'endEdge'
    itemId: null,
    itemType: null, // 'actividad', 'tarea', 'subtarea'
    startX: 0,
    startFechaInicio: null,
    startFechaTermino: null,
    currentDeltaX: 0, // Para preview en tiempo real
  });

  const dragRef = useRef(null);

  const cargarDatos = useCallback(async () => {
    // Si se pasan datos externos, usarlos en lugar de cargar desde el API
    if (actividadesExternas !== null && tareasExternas !== null) {
      setActividades(actividadesExternas);
      setTareas(tareasExternas);
      setLoading(false);
      return;
    }

    // Si no hay datos externos, cargar desde el API usando eventoId
    if (!eventoId) return;

    setLoading(true);
    try {
      const actividadesResponse = await eventosGanttService.actividades.list(eventoId);
      const actividadesData = Array.isArray(actividadesResponse.data)
        ? actividadesResponse.data
        : (actividadesResponse.data.results || []);
      setActividades(actividadesData);

      const tareasResponse = await eventosGanttService.tareas.listByEvento(eventoId);
      const tareasData = Array.isArray(tareasResponse.data)
        ? tareasResponse.data
        : (tareasResponse.data.results || []);
      setTareas(tareasData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar el cronograma');
    } finally {
      setLoading(false);
    }
  }, [eventoId, actividadesExternas, tareasExternas]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Scroll automático a la columna de "hoy" al cargar
  useEffect(() => {
    if (!loading && ganttWrapperRef.current && actividades.length > 0) {
      const { minDate } = obtenerFechaMinMax();
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      // Calcular días desde minDate hasta hoy
      const diasDesdeInicio = Math.floor((hoy - minDate) / (1000 * 60 * 60 * 24));

      if (diasDesdeInicio >= 0) {
        const columnWidth = 80 * zoomLevel / 100;
        const scrollPosition = diasDesdeInicio * columnWidth;

        // Scroll al centro de la columna de hoy
        const wrapperWidth = ganttWrapperRef.current.offsetWidth;
        const centeredScroll = scrollPosition - (wrapperWidth / 2) + (columnWidth / 2) + 300; // +300 por el ancho de la columna de nombres

        ganttWrapperRef.current.scrollLeft = Math.max(0, centeredScroll);
      }
    }
  }, [loading, actividades.length, zoomLevel]);

  // Escuchar el evento personalizado cuando se actualiza una actividad desde el modal
  useEffect(() => {
    const handleActividadActualizada = () => {
      cargarDatos();
    };

    window.addEventListener('actividadActualizada', handleActividadActualizada);
    return () => {
      window.removeEventListener('actividadActualizada', handleActividadActualizada);
    };
  }, [cargarDatos]);

  // Actualizar solo el item afectado (no reload total)
  const actualizarItemLocal = (itemId, itemType, nuevosDatos) => {
    if (itemType === 'actividad') {
      setActividades(prev =>
        prev.map(a => a.id === itemId ? { ...a, ...nuevosDatos } : a)
      );
    } else if (itemType === 'tarea' || itemType === 'subtarea') {
      setTareas(prev =>
        prev.map(t => t.id === itemId ? { ...t, ...nuevosDatos } : t)
      );
    }
  };

  const toggleActivity = (id) => {
    setExpandedActivities(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const obtenerFechaMinMax = () => {
    let minDate = null;
    let maxDate = null;

    actividades.forEach(act => {
      const start = parseDateLocal(act.fecha_inicio);
      const end = parseDateLocal(act.fecha_termino);
      if (!minDate || start < minDate) minDate = start;
      if (!maxDate || end > maxDate) maxDate = end;
    });

    if (!minDate || !maxDate) {
      minDate = new Date();
      maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 1);
    }

    minDate.setDate(minDate.getDate() - 5);
    maxDate.setDate(maxDate.getDate() + 5);

    return { minDate, maxDate };
  };

  const generarDias = () => {
    const { minDate, maxDate } = obtenerFechaMinMax();
    const dias = [];
    const current = new Date(minDate);

    while (current <= maxDate) {
      dias.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dias;
  };

  const calcularPosicion = (fechaInicio, fechaTermino) => {
    const { minDate } = obtenerFechaMinMax();
    const inicio = fechaInicio instanceof Date ? fechaInicio : parseDateLocal(fechaInicio);
    const termino = fechaTermino instanceof Date ? fechaTermino : parseDateLocal(fechaTermino);
    const diasDesdeInicio = Math.floor((inicio - minDate) / (1000 * 60 * 60 * 24));
    const duracion = Math.ceil((termino - inicio) / (1000 * 60 * 60 * 24));
    const columnWidth = 80 * zoomLevel / 100;

    return {
      left: diasDesdeInicio * columnWidth,
      width: Math.max(duracion * columnWidth, 40)
    };
  };

  const formatearFecha = (fecha) => {
    const dateObj = fecha instanceof Date ? fecha : parseDateLocal(fecha);
    return dateObj.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const esHoy = (fecha) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaComparar = fecha instanceof Date ? fecha : parseDateLocal(fecha);
    fechaComparar.setHours(0, 0, 0, 0);
    return hoy.getTime() === fechaComparar.getTime();
  };

  const calcularDiasDesdeX = (deltaX) => {
    const columnWidth = 80 * zoomLevel / 100;
    return Math.round(deltaX / columnWidth);
  };

  // Calcular posición preview durante drag
  const calcularPosicionPreview = (fechaInicio, fechaTermino, tipo = 'move') => {
    const columnWidth = 80 * zoomLevel / 100;
    const daysShift = calcularDiasDesdeX(dragState.currentDeltaX);

    let newStart = parseDateLocal(fechaInicio);
    let newEnd = parseDateLocal(fechaTermino);

    if (tipo === 'move') {
      newStart.setDate(newStart.getDate() + daysShift);
      newEnd.setDate(newEnd.getDate() + daysShift);
    } else if (tipo === 'startEdge') {
      newStart.setDate(newStart.getDate() + daysShift);
    } else if (tipo === 'endEdge') {
      newEnd.setDate(newEnd.getDate() + daysShift);
    }

    return calcularPosicion(newStart, newEnd);
  };

  // Manejo mejorado de drag
  const handleDragStart = (e, itemId, itemType, dragType = 'move') => {
    e.preventDefault();

    let item = null;
    if (itemType === 'actividad') {
      item = actividades.find(a => a.id === itemId);
    } else {
      item = tareas.find(t => t.id === itemId);
    }

    if (!item) return;

    setDragState({
      isDragging: true,
      type: dragType,
      itemId,
      itemType,
      startX: e.clientX,
      startFechaInicio: item.fecha_inicio,
      startFechaTermino: item.fecha_termino,
    });

    dragRef.current = {
      startX: e.clientX,
      currentX: e.clientX,
    };
  };

  const handleMouseMove = (e) => {
    if (!dragState.isDragging || !dragRef.current) return;

    const currentDeltaX = e.clientX - dragState.startX;
    dragRef.current.currentX = e.clientX;

    // Actualizar estado para preview en tiempo real
    setDragState(prev => ({
      ...prev,
      currentDeltaX
    }));
  };

  const handleMouseUp = async (e) => {
    if (!dragState.isDragging) return;

    const deltaX = dragRef.current.currentX - dragRef.current.startX;
    const daysShift = calcularDiasDesdeX(deltaX);

    if (daysShift === 0) {
      setDragState({ isDragging: false, type: null, itemId: null, itemType: null, startX: 0, startFechaInicio: null, startFechaTermino: null });
      dragRef.current = null;
      return;
    }

    try {
      let item = null;
      let updateData = {};

      if (dragState.itemType === 'actividad') {
        item = actividades.find(a => a.id === dragState.itemId);
        if (!item) return;

        const newStart = parseDateLocal(dragState.startFechaInicio);
        const newEnd = parseDateLocal(dragState.startFechaTermino);

        if (dragState.type === 'move') {
          newStart.setDate(newStart.getDate() + daysShift);
          newEnd.setDate(newEnd.getDate() + daysShift);
        } else if (dragState.type === 'startEdge') {
          newStart.setDate(newStart.getDate() + daysShift);
        } else if (dragState.type === 'endEdge') {
          newEnd.setDate(newEnd.getDate() + daysShift);
        }

        updateData = {
          fecha_inicio: newStart.toISOString().split('T')[0],
          fecha_termino: newEnd.toISOString().split('T')[0],
        };

        await eventosGanttService.actividades.update(dragState.itemId, updateData);
        actualizarItemLocal(dragState.itemId, 'actividad', updateData);
        toast.success('Actividad actualizada');

      } else {
        item = tareas.find(t => t.id === dragState.itemId);
        if (!item) return;

        const newStart = parseDateLocal(dragState.startFechaInicio);
        const newEnd = parseDateLocal(dragState.startFechaTermino);

        if (dragState.type === 'move') {
          newStart.setDate(newStart.getDate() + daysShift);
          newEnd.setDate(newEnd.getDate() + daysShift);
        } else if (dragState.type === 'startEdge') {
          newStart.setDate(newStart.getDate() + daysShift);
        } else if (dragState.type === 'endEdge') {
          newEnd.setDate(newEnd.getDate() + daysShift);
        }

        updateData = {
          evento: item.evento,
          actividad: item.actividad,
          fecha_inicio: newStart.toISOString().split('T')[0],
          fecha_termino: newEnd.toISOString().split('T')[0],
          titulo: item.titulo,
        };

        await eventosGanttService.tareas.update(dragState.itemId, updateData);
        actualizarItemLocal(dragState.itemId, dragState.itemType, updateData);
        toast.success('Tarea actualizada');
      }

    } catch (error) {
      console.error('Error al arrastrar:', error);
      toast.error('Error al actualizar');
    } finally {
      setDragState({ isDragging: false, type: null, itemId: null, itemType: null, startX: 0, startFechaInicio: null, startFechaTermino: null, currentDeltaX: 0 });
      dragRef.current = null;
    }
  };

  const tareasDeActividad = (actividadId) => {
    return tareas.filter(t => t.actividad === actividadId && !t.tarea_padre);
  };

  const subtareasDeActividad = (tareaId) => {
    return tareas.filter(t => t.tarea_padre === tareaId);
  };

  const dias = generarDias();
  const anchoColumna = 80 * zoomLevel / 100;
  const anchoTotalDates = dias.length * anchoColumna; // Ancho total de todas las columnas de fechas

  // Helper para renderizar barra con preview durante drag
  const renderBarWithPreview = (item, itemType) => {
    const isDragging = dragState.isDragging && dragState.itemId === item.id && dragState.itemType === itemType;

    if (isDragging && dragState.currentDeltaX !== 0) {
      const previewPos = calcularPosicionPreview(item.fecha_inicio, item.fecha_termino, dragState.type);
      return {
        actual: calcularPosicion(item.fecha_inicio, item.fecha_termino),
        preview: previewPos,
        isDragging: true
      };
    }

    return {
      actual: calcularPosicion(item.fecha_inicio, item.fecha_termino),
      preview: null,
      isDragging: false
    };
  };

  if (loading) {
    return <Loading fullScreen={false} />;
  }

  return (
    <div
      className={`gantt-container ${dragState.isDragging ? 'dragging' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header - Section Style */}
      <div className="section-header gantt-header-section">
        <h3>📊 Cronograma del Evento</h3>
        <div className="gantt-header-actions">
          {/* Toggle Detalles */}
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={mostrarDetalles}
              onChange={(e) => setMostrarDetalles(e.target.checked)}
            />
            <span>Mostrar tareas</span>
          </label>

          {/* Control Zoom */}
          <div className="control-group">
            <label htmlFor="zoomLevel" className="control-label">Zoom:</label>
            <input
              id="zoomLevel"
              type="range"
              min="50"
              max="150"
              value={zoomLevel}
              onChange={(e) => setZoomLevel(parseInt(e.target.value))}
              className="slider"
              title="Ajusta el zoom para ver más o menos fechas"
            />
            <span className="slider-value">{zoomLevel}%</span>
          </div>

          {/* Botones Acciones */}
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              setActividadSeleccionada(null);
              setActividadModalOpen(true);
            }}
            title="Crear nueva actividad"
          >
            ➕ Actividad
          </button>
        </div>
      </div>

      {/* Leyenda */}
      <div className="gantt-legend">
        <div className="legend-item">
          <div className="legend-color legend-actividad"></div>
          <span>Actividades (Partidas)</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-tarea"></div>
          <span>Tareas</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-subtarea"></div>
          <span>Subtareas</span>
        </div>
        <div className="legend-item legend-info">
          <span>💡 Haz clic para editar • Arrastra el centro para mover • Arrastra bordes para redimensionar</span>
        </div>
      </div>

      {/* Timeline Container */}
      <div className="gantt-chart-wrapper" ref={ganttWrapperRef}>
        {actividades.length === 0 ? (
          <div className="gantt-empty">
            <p>📭 No hay actividades para mostrar</p>
            <button
              className="btn btn-primary"
              onClick={() => setActividadModalOpen(true)}
            >
              Crear Primera Actividad
            </button>
          </div>
        ) : (
          <div className="gantt-timeline" style={{ minWidth: `${300 + anchoTotalDates}px` }}>
            {/* Encabezado de fechas */}
            <div className="gantt-header-row">
              <div className="gantt-names" style={{ width: '300px', minWidth: '300px' }}>
                <div className="gantt-name-header">Tarea / Actividad</div>
              </div>
              <div className="gantt-dates" style={{ display: 'flex', width: `${anchoTotalDates}px`, minWidth: `${anchoTotalDates}px` }}>
                {dias.map((dia, idx) => {
                  const isToday = esHoy(dia);
                  return (
                    <div
                      key={idx}
                      className="gantt-date-header"
                      style={{
                        width: `${anchoColumna}px`,
                        minWidth: `${anchoColumna}px`,
                        backgroundColor: isToday ? '#dbeafe' : undefined,
                        borderLeft: isToday ? '2px solid #3b82f6' : undefined,
                        borderRight: isToday ? '2px solid #3b82f6' : undefined,
                      }}
                    >
                      <div className="date-day" style={{ color: isToday ? '#1e40af' : undefined, fontWeight: isToday ? '700' : undefined }}>
                        {dia.toLocaleDateString('es-ES', { weekday: 'short' })}
                      </div>
                      <div className="date-number" style={{ color: isToday ? '#1e40af' : undefined, fontWeight: isToday ? '800' : undefined }}>
                        {dia.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Filas de actividades y tareas */}
            {actividades.map(actividad => (
              <div key={`actividad-${actividad.id}`} className="gantt-item actividad-group">
                {/* Fila de Actividad */}
                <div
                  className="gantt-row actividad-row"
                  onClick={() => {
                    toggleActivity(actividad.id);
                    setActividadSeleccionada(actividad);
                  }}
                >
                  <div className="gantt-names" style={{ width: '300px', minWidth: '300px' }}>
                    <div className="gantt-name-content">
                      <button
                        className={`expand-btn${expandedActivities[actividad.id] ? ' is-expanded' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActivity(actividad.id);
                        }}
                        title={expandedActivities[actividad.id] ? 'Colapsar' : 'Expandir'}
                      />
                      <span
                        className="activity-name"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActividadSeleccionada(actividad);
                          setActividadModalOpen(true);
                        }}
                        title="Click para editar actividad"
                      >
                        {actividad.titulo}
                        {actividad.proyecto_nombre && (
                          <span style={{
                            marginLeft: '0.25rem',
                            fontSize: '0.7rem',
                            color: '#6b7280',
                            fontWeight: 'normal',
                            flexShrink: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '80px',
                          }}>
                            [{actividad.proyecto_nombre}]
                          </span>
                        )}
                      </span>
                      <button
                        className="btn btn-xs btn-add-task"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActividadPadreParaTarea(actividad);
                          setTareaSeleccionada(null);
                          setTareaModalOpen(true);
                        }}
                        title="Agregar tarea a esta actividad"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="gantt-dates" style={{ display: 'flex', width: `${anchoTotalDates}px`, minWidth: `${anchoTotalDates}px`, position: 'relative' }}>
                    {/* Renderizar celdas de fondo para cada día */}
                    {dias.map((dia, idx) => {
                      const isToday = esHoy(dia);
                      return (
                        <div
                          key={idx}
                          className="gantt-date-cell"
                          style={{
                            width: `${anchoColumna}px`,
                            minWidth: `${anchoColumna}px`,
                            backgroundColor: isToday ? '#eff6ff' : undefined,
                            borderLeft: isToday ? '2px solid #3b82f6' : undefined,
                            borderRight: isToday ? '2px solid #3b82f6' : undefined,
                          }}
                        />
                      );
                    })}
                    {/* Barras de gantt posicionadas absolutamente encima */}
                    {(() => {
                      const barData = renderBarWithPreview(actividad, 'actividad');
                      return (
                        <>
                          {/* Barra de preview (ghosted) durante drag */}
                          {barData.isDragging && barData.preview && (
                            <div
                              className="gantt-bar actividad-bar gantt-bar-preview"
                              style={{
                                ...barData.preview,
                                backgroundColor: actividad.color || '#3b82f6',
                                height: '28px',
                                marginTop: '8px',
                                opacity: 0.4,
                                border: '2px dashed rgba(255,255,255,0.5)',
                                position: 'absolute',
                                top: 0
                              }}
                            >
                              <div className="bar-progress" style={{
                                width: `${Math.min(actividad.porcentaje_avance || 0, 100)}%`,
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                height: '100%',
                                borderRadius: '2px'
                              }}></div>
                            </div>
                          )}

                          {/* Barra actual */}
                          <div
                            className={`gantt-bar actividad-bar ${barData.isDragging ? 'bar-dragging' : ''}`}
                            style={{
                              ...barData.actual,
                              backgroundColor: actividad.color || '#3b82f6',
                              height: '28px',
                              marginTop: '8px',
                              opacity: barData.isDragging ? 0.7 : 1,
                              position: 'absolute',
                              top: 0
                            }}
                            title={`${formatearFecha(actividad.fecha_inicio)} - ${formatearFecha(actividad.fecha_termino)} (${Math.round(actividad.porcentaje_avance) || 0}%)`}
                            onMouseDown={(e) => handleDragStart(e, actividad.id, 'actividad', 'move')}
                          >
                            {/* Borde izquierdo para redimensionar */}
                            <div
                              className="bar-edge bar-edge-left"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleDragStart(e, actividad.id, 'actividad', 'startEdge');
                              }}
                              title="Arrastra para cambiar fecha de inicio"
                            />

                            <div className="bar-progress" style={{
                              width: `${Math.min(actividad.porcentaje_avance || 0, 100)}%`,
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              height: '100%',
                              borderRadius: '2px'
                            }}></div>
                            <span className="bar-label">
                              {actividad.titulo}
                              {actividad.proyecto_nombre && ` [${actividad.proyecto_nombre}]`}
                            </span>

                            {/* Borde derecho para redimensionar */}
                            <div
                              className="bar-edge bar-edge-right"
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                handleDragStart(e, actividad.id, 'actividad', 'endEdge');
                              }}
                              title="Arrastra para cambiar fecha de término"
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Tareas de la actividad */}
                {expandedActivities[actividad.id] && mostrarDetalles && (
                  <div className="tareas-container">
                    {tareasDeActividad(actividad.id).map(tarea => (
                      <div key={`tarea-${tarea.id}`} className="gantt-item tarea-group">
                        {/* Fila de Tarea */}
                        <div
                          className="gantt-row tarea-row"
                          onClick={() => {
                            toggleActivity(`subtarea-${tarea.id}`);
                            setTareaSeleccionada(tarea);
                          }}
                        >
                          <div className="gantt-names" style={{ width: '300px', minWidth: '300px' }}>
                            <div className="gantt-name-content indent-1">
                              {subtareasDeActividad(tarea.id).length > 0 && (
                                <button
                                  className={`expand-btn${expandedActivities[`subtarea-${tarea.id}`] ? ' is-expanded' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleActivity(`subtarea-${tarea.id}`);
                                  }}
                                  title={expandedActivities[`subtarea-${tarea.id}`] ? 'Colapsar' : 'Expandir'}
                                />
                              )}
                              <span
                                className="task-name"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTareaSeleccionada(tarea);
                                  setTareaModalOpen(true);
                                }}
                                title="Click para editar tarea"
                              >
                                {tarea.titulo}
                                {tarea.proyecto_nombre && (
                                  <span style={{
                                    marginLeft: '0.25rem',
                                    fontSize: '0.65rem',
                                    color: '#6b7280',
                                    fontWeight: 'normal',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '70px',
                                  }}>
                                    [{tarea.proyecto_nombre}]
                                  </span>
                                )}
                              </span>
                              {tarea.usuario_asignado_nombre && (
                                <span className="task-user">👤 {tarea.usuario_asignado_nombre}</span>
                              )}
                              <button
                                className="btn btn-xs btn-add-task"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTareaSeleccionada(null);
                                  setTareaModalOpen(true);
                                }}
                                title="Agregar subtarea"
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <div className="gantt-dates" style={{ display: 'flex', width: `${anchoTotalDates}px`, minWidth: `${anchoTotalDates}px`, position: 'relative' }}>
                            {/* Renderizar celdas de fondo para cada día */}
                            {dias.map((dia, idx) => {
                              const isToday = esHoy(dia);
                              return (
                                <div
                                  key={idx}
                                  className="gantt-date-cell"
                                  style={{
                                    width: `${anchoColumna}px`,
                                    minWidth: `${anchoColumna}px`,
                                    backgroundColor: isToday ? '#eff6ff' : undefined,
                                    borderLeft: isToday ? '2px solid #3b82f6' : undefined,
                                    borderRight: isToday ? '2px solid #3b82f6' : undefined,
                                  }}
                                />
                              );
                            })}
                            {/* Barras de gantt posicionadas absolutamente encima */}
                            {(() => {
                              const barData = renderBarWithPreview(tarea, 'tarea');
                              return (
                                <>
                                  {/* Barra de preview durante drag */}
                                  {barData.isDragging && barData.preview && (
                                    <div
                                      className="gantt-bar tarea-bar gantt-bar-preview"
                                      style={{
                                        ...barData.preview,
                                        backgroundColor: '#10b981',
                                        height: '24px',
                                        marginTop: '10px',
                                        opacity: 0.4,
                                        border: '2px dashed rgba(255,255,255,0.5)',
                                        position: 'absolute',
                                        top: 0
                                      }}
                                    >
                                      <div className="bar-progress" style={{
                                        width: `${Math.min(tarea.porcentaje_avance || 0, 100)}%`,
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                        height: '100%',
                                        borderRadius: '1px'
                                      }}></div>
                                    </div>
                                  )}

                                  {/* Barra actual */}
                                  <div
                                    className={`gantt-bar tarea-bar ${barData.isDragging ? 'bar-dragging' : ''}`}
                                    style={{
                                      ...barData.actual,
                                      backgroundColor: '#10b981',
                                      height: '24px',
                                      marginTop: '10px',
                                      cursor: 'grab',
                                      opacity: barData.isDragging ? 0.7 : 1,
                                      position: 'absolute',
                                      top: 0
                                    }}
                                    onMouseDown={(e) => handleDragStart(e, tarea.id, 'tarea', 'move')}
                                    title={`${formatearFecha(tarea.fecha_inicio)} - ${formatearFecha(tarea.fecha_termino)} (${Math.round(tarea.porcentaje_avance) || 0}%)`}
                                  >
                                    {/* Borde izquierdo */}
                                    <div
                                      className="bar-edge bar-edge-left"
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleDragStart(e, tarea.id, 'tarea', 'startEdge');
                                      }}
                                      title="Arrastra para cambiar fecha de inicio"
                                    />

                                    <div className="bar-progress" style={{
                                      width: `${Math.min(tarea.porcentaje_avance || 0, 100)}%`,
                                      backgroundColor: 'rgba(0,0,0,0.2)',
                                      height: '100%',
                                      borderRadius: '1px'
                                    }}></div>
                                    <span className="bar-label">
                                      {tarea.titulo}
                                      {tarea.proyecto_nombre && ` [${tarea.proyecto_nombre}]`}
                                    </span>

                                    {/* Borde derecho */}
                                    <div
                                      className="bar-edge bar-edge-right"
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleDragStart(e, tarea.id, 'tarea', 'endEdge');
                                      }}
                                      title="Arrastra para cambiar fecha de término"
                                    />
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Subtareas */}
                        {expandedActivities[`subtarea-${tarea.id}`] && (
                          <div className="subtareas-container">
                            {subtareasDeActividad(tarea.id).map(subtarea => (
                              <div key={`subtarea-${subtarea.id}`} className="gantt-row subtarea-row">
                                <div className="gantt-names" style={{ width: '300px', minWidth: '300px' }}>
                                  <div className="gantt-name-content indent-2">
                                    <span
                                      className="subtask-name"
                                      onClick={() => {
                                        setTareaSeleccionada(subtarea);
                                        setTareaModalOpen(true);
                                      }}
                                      title="Click para editar subtarea"
                                    >
                                      {subtarea.titulo}
                                    </span>
                                  </div>
                                </div>
                                <div className="gantt-dates" style={{ display: 'flex', width: `${anchoTotalDates}px`, minWidth: `${anchoTotalDates}px`, position: 'relative' }}>
                                  {/* Renderizar celdas de fondo para cada día */}
                                  {dias.map((dia, idx) => {
                                    const isToday = esHoy(dia);
                                    return (
                                      <div
                                        key={idx}
                                        className="gantt-date-cell"
                                        style={{
                                          width: `${anchoColumna}px`,
                                          minWidth: `${anchoColumna}px`,
                                          backgroundColor: isToday ? '#eff6ff' : undefined,
                                          borderLeft: isToday ? '2px solid #3b82f6' : undefined,
                                          borderRight: isToday ? '2px solid #3b82f6' : undefined,
                                        }}
                                      />
                                    );
                                  })}
                                  {/* Barras de gantt posicionadas absolutamente encima */}
                                  {(() => {
                                    const barData = renderBarWithPreview(subtarea, 'subtarea');
                                    return (
                                      <>
                                        {/* Barra de preview durante drag */}
                                        {barData.isDragging && barData.preview && (
                                          <div
                                            className="gantt-bar subtarea-bar gantt-bar-preview"
                                            style={{
                                              ...barData.preview,
                                              backgroundColor: '#69B32D',
                                              height: '20px',
                                              marginTop: '12px',
                                              opacity: 0.4,
                                              border: '2px dashed rgba(255,255,255,0.5)',
                                              position: 'absolute',
                                              top: 0
                                            }}
                                          >
                                            <div className="bar-progress" style={{
                                              width: `${Math.min(subtarea.porcentaje_avance || 0, 100)}%`,
                                              backgroundColor: 'rgba(0,0,0,0.2)',
                                              height: '100%',
                                              borderRadius: '1px'
                                            }}></div>
                                          </div>
                                        )}

                                        {/* Barra actual */}
                                        <div
                                          className={`gantt-bar subtarea-bar ${barData.isDragging ? 'bar-dragging' : ''}`}
                                          style={{
                                            ...barData.actual,
                                            backgroundColor: '#69B32D',
                                            height: '20px',
                                            marginTop: '12px',
                                            cursor: 'grab',
                                            opacity: barData.isDragging ? 0.7 : 1,
                                            position: 'absolute',
                                            top: 0
                                          }}
                                          onMouseDown={(e) => handleDragStart(e, subtarea.id, 'subtarea', 'move')}
                                          title={`${formatearFecha(subtarea.fecha_inicio)} - ${formatearFecha(subtarea.fecha_termino)} (${Math.round(subtarea.porcentaje_avance) || 0}%)`}
                                        >
                                          {/* Borde izquierdo */}
                                          <div
                                            className="bar-edge bar-edge-left"
                                            onMouseDown={(e) => {
                                              e.stopPropagation();
                                              handleDragStart(e, subtarea.id, 'subtarea', 'startEdge');
                                            }}
                                          />

                                          <div className="bar-progress" style={{
                                            width: `${Math.min(subtarea.porcentaje_avance || 0, 100)}%`,
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            height: '100%',
                                            borderRadius: '1px'
                                          }}></div>
                                          <span className="bar-label">{subtarea.titulo}</span>

                                          {/* Borde derecho */}
                                          <div
                                            className="bar-edge bar-edge-right"
                                            onMouseDown={(e) => {
                                              e.stopPropagation();
                                              handleDragStart(e, subtarea.id, 'subtarea', 'endEdge');
                                            }}
                                          />
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modales */}
      <ActividadModal
        isOpen={actividadModalOpen}
        onClose={() => {
          setActividadModalOpen(false);
          setActividadSeleccionada(null);
        }}
        eventoId={eventoId}
        actividad={actividadSeleccionada}
        onSave={cargarDatos}
      />

      <TareaEventoModal
        isOpen={tareaModalOpen}
        onClose={() => {
          setTareaModalOpen(false);
          setTareaSeleccionada(null);
          setActividadPadreParaTarea(null);
        }}
        eventoId={eventoId}
        actividadId={actividadPadreParaTarea?.id || tareaSeleccionada?.actividad}
        tarea={tareaSeleccionada}
        tareaParentId={tareaSeleccionada?.id}
        onSave={cargarDatos}
      />
    </div>
  );
};

export default GanttChart;
